import { useEffect, useMemo, useState } from 'react'
import {
  calculateForeground,
  ContentType,
  hexToRGBA,
  TopSectionData,
} from '@bm-js/atlascloud-shared'
import { textAligns } from '../utils/typography'
import { LinkComponentType } from './CLink'
import { CustomerButton } from './CustomerButton'
import { ResponsiveImage } from './ResponsiveImage'

type Props = {
  content: ContentType<TopSectionData>
  LinkComponent: LinkComponentType
}

export const TopSection = ({ content, LinkComponent }: Props) => {
  const VIEW_TIME = 5000

  const [currentSlide, setCurrentSlide] = useState(0)

  const switchImage = () => {
    if (content.data.images.length < 1) return
    const current = parseInt(
      localStorage.getItem(`currentSlide_${content.uuid}`) || '0'
    )
    if (content.data.images.length === 1) {
      setCurrentSlide(current)
      return
    }
    const next = current + 1 >= content.data.images.length ? 0 : current + 1
    setCurrentSlide(next)
    localStorage.setItem(`currentSlide_${content.uuid}`, String(next))
  }

  const getColorData = (val: string) => {
    switch (val) {
      case 'white':
        return '#FFFFFF'
      case 'black':
        return '#000000'
      case 'gray200':
        return '#F2F4F7'
      case 'gray400':
        return '#D0D5DD'
      case 'gray600':
        return '#667085'
      default:
        return '#FFFFFF'
    }
  }

  const headerHtml = useMemo(() => {
    const { headerText, headerHighlight } = content.data
    if (!headerText || !headerHighlight) return headerText
    const match = headerText.match(new RegExp(headerHighlight, 'gi'))
    if (!match) return headerText
    const parts = headerText.split(match[0])
    return (
      <>
        {parts[0]}
        <span className='text-customerAccent-400'>{match[0]}</span>
        {parts[1]}
      </>
    )
  }, [content.data.headerText, content.data.headerHighlight])

  const alignmentClasses = useMemo(() => {
    return textAligns[content.data.alignment || 'left']
  }, [content.data.alignment])

  useEffect(() => {
    const currentElement = document.getElementById(
      `sliderImage_${content.data.images[currentSlide]?.uuid}`
    )
    if (!currentElement) return
    if (content.data.images.length === 1) {
      currentElement.style.opacity = '1'
      return
    }
    if (content.data.images.length < 2) return
    const next =
      currentSlide + 1 >= content.data.images.length ? 0 : currentSlide + 1
    const nextElement = document.getElementById(
      `sliderImage_${content.data.images[next].uuid}`
    )
    if (!nextElement) return
    currentElement.style.opacity = '0'
    nextElement.style.opacity = '1'
  }, [currentSlide, content.data.images])

  useEffect(() => {
    if (content.data.images.length === 1) return switchImage()
    if (content.data.images.length < 2) return
    localStorage.setItem(`currentSlide_${content.uuid}`, '0')
    const interval = setInterval(switchImage, VIEW_TIME)
    return () => {
      clearInterval(interval)
    }

    //eslint-disable-next-line
  }, [content.data.images])

  return (
    <div
      className='relative mb-12'
      style={{
        minHeight: content.data.minHeight || '40vh',
        marginBottom: content.data.marginBottom,
      }}
    >
      {content.data.images.length ? (
        <div className='relative w-full'>
          {content.data.images.map((image) => (
            <div
              className='absolute w-full opacity-0 transition-all duration-700'
              key={`sliderImage_${image.uuid}`}
              id={`sliderImage_${image.uuid}`}
            >
              <ResponsiveImage
                className='object-cover w-full'
                style={{ height: content.data.minHeight || '40vh' }}
                file={image}
              />
            </div>
          ))}
        </div>
      ) : null}
      <div
        className='flex items-center p-4 inset-0 h-full absolute'
        style={{
          background: hexToRGBA(
            getColorData(content.data.overlayColor),
            content.data.overlayStrength
          ),
          color: calculateForeground(getColorData(content.data.overlayColor)),
        }}
      >
        <div
          className={`w-full max-w-[1000px] p-4 mx-auto ${alignmentClasses}`}
        >
          <h1
            className={`m-0 font-semibold max-w-[60ch] md:text-[2.5rem] text-[2rem] mb-1 leading-tight ${alignmentClasses}`}
          >
            {headerHtml}
          </h1>
          <p
            className={`m-0 text-[1.1rem] max-w-[60ch] leading-6 font-normal ${alignmentClasses}`}
          >
            {content.data.paragraphText}
          </p>
          {content.data.ctaText && content.data.ctaLink ? (
            <LinkComponent href={content.data.ctaLink}>
              <CustomerButton
                className='mt-4'
                btnColor={
                  content.data.ctaColor === 'accent' ? 'accent' : 'customer'
                }
              >
                {content.data.ctaText}
              </CustomerButton>
            </LinkComponent>
          ) : null}
        </div>
      </div>
    </div>
  )
}
